<template>
  <v-app>
    <app-status-popup></app-status-popup>
    <main-loading v-show="profileLoading"></main-loading>
    <nav-panel-drawer v-if="profileHasMap && !showFactSheet"></nav-panel-drawer>
    <v-app-bar v-if="profileHasMap && !showFactSheet" app flat short>
      <v-app-bar-nav-icon
        app
        :ripple="false"
        @click="toggleDrawer"
        class="ma-1 ml-n4 theme--light elevation-4 rounded-right"
        tile
        width="36px"
      >
        <v-icon :value="drawer">
          {{ drawer ? 'mdi-chevron-left' : 'mdi-chevron-right' }}
        </v-icon>
      </v-app-bar-nav-icon>
      <v-spacer></v-spacer>
      <brand-logo></brand-logo>
    </v-app-bar>
    <v-content>
      <main-loading v-show="loading" show="true"></main-loading>
      <main-map v-show="showMap"></main-map>
      <main-list v-if="showList"></main-list>
      <main-nonmap v-if="showFactSheet"></main-nonmap>
    </v-content>
    <app-footer v-if="profileHasMap && !showFactSheet" />
  </v-app>
</template>

<script>
import AppStatusPopup from '@/components/app-status-popup';
import NavPanelDrawer from '@/components/nav-panel-drawer';
import BrandLogo from '@/components/brand-logo';
import AppFooter from '@/components/app-footer';
import mainMap from '@/components/main-map';
import mainList from '@/components/main-list';
import MainLoading from '@/components/main-loading';
import MainNonmap from '@/components/main-nonmap';

import Profile from '@/api/Profile';
import appConfig from '@/config/appConfig';

import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';

import { loadModules } from 'esri-loader';

import Countries from '@/api/Countries';
import TechData from '@/api/TechData';
import ClientsideFacilities from '@/api/ClientsideFacilities';
import FeatureLayerHelper from './helpers/FeatureLayerHelper';
import mapConfig from './config/mapConfig';

const appendJsonExtension = (filename) => {
  return filename.endsWith('.json') ? filename : `${filename}.json`;
};

export default {
  name: 'App',
  components: {
    AppStatusPopup,
    NavPanelDrawer,
    BrandLogo,
    AppFooter,
    mainMap,
    mainList,
    MainLoading,
    MainNonmap
  },
  data: () => ({ profileLoading: false }),
  computed: {
    ...mapState({
      drawer: 'drawer'
    }),
    ...mapGetters({
      currentView: 'currentView',
      mapTemplate: 'profileConfig/mapTemplate',
      profileHasMap: 'profileConfig/profileHasMap',
      getErrors: 'errors',
      loading: 'mapTemplate/loading',
      isMobile: 'mapTemplate/isMobile',
      mapProfileLoaded: 'profileConfig/mapProfileLoaded',
      assetsLoaded: 'filterData/assetTypesLoaded',
      profileDataLoaded: 'profileConfig/profileDataLoaded'
    }),
    showMap: function () {
      return (
        !this.profileLoading &&
        this.profileHasMap &&
        this.currentView === 'MapView'
      );
    },
    showList: function () {
      return (
        !this.profileLoading &&
        this.profileHasMap &&
        this.currentView === 'ListView'
      );
    },
    showFactSheet: function () {
      return (
        !this.profileLoading &&
        this.profileHasMap &&
        this.currentView === 'FactSheet'
      );
    }
  },
  methods: {
    ...mapActions('filterData', ['fetchAssetTypes', 'fetchLocations']),
    ...mapActions({
      setFacilityStateValues: 'mapTemplate/setFacilityStateValues',
      setLocationStateValues: 'mapTemplate/setLocationStateValues',
      updateTableData: 'tableData/updateTableData',
      initializeGeographyTemplate:
        'geographyTemplate/initializeGeographyTemplate',
      selectLocation: 'geographyTemplate/selectLocation',
      selectFacility: 'facilityTemplate/selectFacility'
    }),
    ...mapMutations({
      setPipelineFeatureLayer: 'mapTemplate/setPipelineFeatureLayer',
      setTransmissionFeatureLayer: 'mapTemplate/setTransmissionFeatureLayer',
      initializeMapTemplateState: 'mapTemplate/initializeMapTemplateState',
      toggleDrawer: 'toggleDrawer',
      setCurrentView: 'setCurrentView',
      setProfile: 'profileConfig/setProfile',
      setProfileLoaded: 'profileConfig/setProfileLoaded',
      setMapProfileLoaded: 'profileConfig/setMapProfileLoaded',
      setProfileDataLoaded: 'profileConfig/setProfileDataLoaded',
      setCountryExtents: 'mapTemplate/setCountryExtents',
      setCountryData: 'mapTemplate/setCountryData',
      setTechData: 'facilityTemplate/setTechData',
      setError: 'setError',
      setLoading: 'mapTemplate/setLoading',
      setStateDefaultProfile: 'profileConfig/setStateDefaultProfile',
      setFacilityDefaultProfile: 'profileConfig/setFacilityDefaultProfile',
      setIsMobile: 'mapTemplate/setIsMobile'
    })
  },
  created() {
    // IMPORTANT - Loading the Esri CSS as early as possible is critical in
    // avoiding the map scrolling/height rendering issue.
    loadModules(['esri/views/MapView'], {
      css: true
    }).then(([MapView]) => {});
    // Load app with drawer closed on small devices so map renders full screen.
    if (this.$vuetify.breakpoint.smAndDown && this.drawer != false) {
      this.toggleDrawer();
    }
  },
  mounted() {
    let analytics = document.createElement('script');
    analytics.setAttribute(
      'src',
      '//assets.adobedtm.com/launch-ENeb8976992cfa4614b1ec02b237aaa848-development.min.js'
    );
    document.head.appendChild(analytics);
    // Load profile if specified on url, otherwise use default map profile.
    const locationName = this.$route.query.l ? this.$route.query.l : undefined;
    const facilityName = this.$route.query.f ? this.$route.query.f : undefined;

    if (locationName || facilityName) {
      //console.log('fact sheet');
      this.setCurrentView('FactSheet');
    }

    this.setIsMobile(
      //true
      window.matchMedia('only screen and (max-width: 760px)').matches
    );

    const getProfile = async () => {
      this.profileLoading = true;
      const profile = new Profile(appConfig.profileApiAxiosConfig);

      const [
        profileData,
        stateDefaultProfileData,
        facilityDefaultProfileData
      ] = await Promise.all([
        profile.get(appendJsonExtension(appConfig.defaultMapProfile)),
        profile.get(appendJsonExtension(appConfig.defaultStateProfile)),
        profile.get(appendJsonExtension(appConfig.defaultFacilityProfile))
      ]).catch((err) => {
        console.log(`Error getting application profiles:`);
        console.log(err);
        this.setError('Profile: ' + err.message);
      });

      this.profileLoading = false;
      this.setProfile(profileData);
      this.setStateDefaultProfile(stateDefaultProfileData);
      this.setFacilityDefaultProfile(facilityDefaultProfileData);
      this.setProfileLoaded(true);

      // Get States, Facilities, and Country data and commit to Vuex
      const countries = new Countries(appConfig.countriesApiProps);
      const countryData = await countries.getCountries().catch((err) => {
        console.log(`Error getting country data:`, err);
        this.setError('Country Data: ' + err.message);
      });

      this.setCountryData(countryData);

      const tech = new TechData(appConfig.techDataApiProps);
      const techData = await tech.getTechData().catch((err) => {
        console.log(`Error getting country data:`, err);
        this.setError('Tech Data: ' + err.message);
      });

      this.setTechData(techData);

      const [
        clientsideLocationsFeatureLayer,
        clientsideFacilitiesFeatureLayer,
        clientsideFacilitiesPipelineFeatureLayer,
        clientsideFacilitiesTransmissionFeatureLayer
      ] = await Promise.all([
        FeatureLayerHelper.createClientsideFeatureLayer(
          mapConfig.nextEraLocationsFeatureLayerDef
        ),
        FeatureLayerHelper.createClientsideFeatureLayer(
          mapConfig.nextEraFacilitiesFeatureLayerDef
        ),
        FeatureLayerHelper.createClientsideFeatureLayer(
          mapConfig.nextEraPipelinesFeatureLayerDef
        ),
        FeatureLayerHelper.createClientsideFeatureLayer(
          mapConfig.nextEraTransmissionFeatureLayerDef
        )
      ]).catch((err) => {
        console.log(`Error getting Map Service Data:`);
        console.log(err);
        this.setError('State/Facility: ' + err.message);
      });

      this.setLocationStateValues({
        featureLayer: clientsideLocationsFeatureLayer
      });
      this.setFacilityStateValues({
        featureLayer: clientsideFacilitiesFeatureLayer
      });

      this.setPipelineFeatureLayer(clientsideFacilitiesPipelineFeatureLayer);
      this.setTransmissionFeatureLayer(
        clientsideFacilitiesTransmissionFeatureLayer
      );

      this.fetchAssetTypes();

      this.setProfileDataLoaded(true);

      if (this.profileHasMap && !facilityName && !locationName) {
        this.initializeMapTemplateState(this.mapTemplate);

        // Derive Global/USA/Canada extents - these extents are derived from facilities
        // within the given geometry, not actual country or state/province boundaries.
        const clientsideFacilities = new ClientsideFacilities(
          appConfig.facilityApiProps
        );
        const continentalUsaFacilitiesExtent = await clientsideFacilities.getContinentalUsaFacilitiesExtent(
          clientsideFacilitiesFeatureLayer
        );
        const continentalNAFacilitiesExtent = await clientsideFacilities.getContinentalNorthAmericaFacilitiesExtent(
          clientsideFacilitiesFeatureLayer
        );
        const canadaFacilitiesExtent = await clientsideFacilities.getCanadaFacilitiesExtent(
          clientsideFacilitiesFeatureLayer
        );
        const countryExtents = new Array(
          {
            name: appConfig.countriesApiProps.usaCountryValue,
            extent: continentalUsaFacilitiesExtent
          },
          {
            name: appConfig.countriesApiProps.canadaCountryValue,
            extent: canadaFacilitiesExtent
          },
          {
            name: appConfig.countriesApiProps.globalCountryValue,
            extent: continentalNAFacilitiesExtent
          }
        );
        this.setCountryExtents(countryExtents);

        this.setMapProfileLoaded(true);
        return;
      }
      if (locationName) {
        //console.log('Loaction Loading: ' + locationName);
        //This will set the location fact view if sent in the querystring
        this.selectLocation(locationName);
        return;
      }
      if (facilityName) {
        //TODO: Add loading of State Fact Sheet data
        //console.log('Facility Loading: ' + facilityName);
        this.selectFacility(facilityName);
        return;
      }
    };
    getProfile();
  },
  watch: {
    mapProfileLoaded: function (newValue, oldValue) {
      if (newValue && this.assetsLoaded) {
        this.updateTableData();
      }
    },
    assetsLoaded: function (newValue, oldValue) {
      if (
        (newValue && this.mapProfileLoaded) ||
        (this.profileDataLoaded && this.currentView === 'FactSheet')
      ) {
        this.updateTableData();
      }
    },
    profileDataLoaded: function (newValue, oldValue) {
      if (newValue && this.assetsLoaded && this.currentView === 'FactSheet') {
        this.updateTableData();
      }
    }
  },
  errorCaptured(err, vm, info) {
    console.warn('ERROR CAPTURED in APP.VUE: ', err, vm, info);
  }
};
</script>

<style lang="scss">
.logo-nextera {
  width: auto;
  padding-top: 10px;
}
button.v-app-bar__nav-icon {
  border-radius: 0 $border-radius-root $border-radius-root 0;
  background-color: $ne-lightgray !important;
  &:hover,
  &:focus,
  &:active {
    &::before {
      background-color: $ne-lightgray !important;
      opacity: 0 !important;
    }
  }
}
.v-system-bar {
  padding: 0 0 !important;
}
</style>
